import type { RelationshipType, DataConsent, WotSelection } from './sessionTypes'
import { DEMO_BABBALONIA_CLIENT, DEMO_BABBALONIA_ID } from './demoClients'

export type ClientTrack = 'practice' | 'research'

export type NameIdentifierType = 'name' | 'pseudonym' | 'reference'

export interface SessionLogEntry {
  id: string
  /** ISO date string (YYYY-MM-DD) */
  date: string
  practices?: string[]
  wotStart: WotSelection
  wotEnd: WotSelection
  panasStart: number
  panasEnd: number
  practitionerNotes: string
  followUpNotes?: string
}

export interface ClientRecord {
  id: string
  name: string
  nameIdentifierType: NameIdentifierType
  sessionType: RelationshipType
  track: ClientTrack
  dataConsent: DataConsent
  notes?: string
  sessionHistory?: SessionLogEntry[]
}

export const CLIENTS_KEY = 'fitutogether-clients'

export const SESSION_TYPE_LABELS: Record<RelationshipType, string> = {
  'practitioner-client': 'Practitioner and client',
  'parent-child':        'Practitioner, parent and child',
  'co-workers':          'Peers',
  'other':               'Something else',
}

export const TRACK_LABELS: Record<ClientTrack, string> = {
  practice: 'Practice only',
  research: 'Research',
}

export const DATA_CONSENT_LABELS: Record<DataConsent, string> = {
  yes: "Yes, I'm happy for my anonymised data to be used for research",
  no:  'No thank you, just save my session notes',
}

export function readClients(): ClientRecord[] {
  try {
    const raw = localStorage.getItem(CLIENTS_KEY)
    const parsed = raw ? (JSON.parse(raw) as Partial<ClientRecord>[]) : []
    const normalized = parsed.map(c => ({
      notes: '',
      dataConsent: 'no' as DataConsent,
      nameIdentifierType: 'name' as NameIdentifierType,
      ...c,
    })) as ClientRecord[]

    const { clients, changed } = ensureDemoClients(normalized)
    if (changed) saveClients(clients)
    return clients
  } catch {
    const { clients, changed } = ensureDemoClients([])
    if (changed) saveClients(clients)
    return clients
  }
}

function ensureDemoClients(
  clients: ClientRecord[],
): { clients: ClientRecord[]; changed: boolean } {
  const demo = DEMO_BABBALONIA_CLIENT
  const idx = clients.findIndex(c => c.id === DEMO_BABBALONIA_ID)

  if (idx === -1) {
    return { clients: [demo, ...clients], changed: true }
  }

  const existing = clients[idx]
  const needsHistory = !existing.sessionHistory?.length
  const needsNotes = !existing.notes?.trim()

  if (!needsHistory && !needsNotes) {
    return { clients, changed: false }
  }

  const updated: ClientRecord = {
    ...existing,
    sessionHistory: needsHistory ? demo.sessionHistory : existing.sessionHistory,
    notes: needsNotes ? demo.notes : existing.notes,
  }

  const next = [...clients]
  next[idx] = updated
  return { clients: next, changed: true }
}

export function saveClients(clients: ClientRecord[]): void {
  localStorage.setItem(CLIENTS_KEY, JSON.stringify(clients))
}

export function addClient(client: Omit<ClientRecord, 'id'>): ClientRecord {
  const newClient: ClientRecord = {
    notes: '',
    nameIdentifierType: 'name',
    ...client,
    id: crypto.randomUUID(),
  }
  saveClients([...readClients(), newClient])
  return newClient
}

export function getClientById(id: string): ClientRecord | undefined {
  return readClients().find(c => c.id === id)
}

export function updateClient(
  id: string,
  update: Partial<Omit<ClientRecord, 'id'>>,
): ClientRecord | undefined {
  const clients = readClients()
  const idx = clients.findIndex(c => c.id === id)
  if (idx === -1) return undefined
  clients[idx] = { ...clients[idx], ...update }
  saveClients(clients)
  return clients[idx]
}
